import { ActionIconProps, Mark, Text, UnstyledButton } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, forwardRef } from "react";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { deleteIdeia } from "../../firestore";
import { QUERY_KEY } from "../ideias-table/IdeiasTable";

type RemoverButtonProperties = PropsWithChildren<
  ActionIconProps & { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; ideia: TIdeia }
>;
type TDeleteResponse = Awaited<ReturnType<typeof deleteIdeia>>;

const RemoverIdeiaButton = forwardRef<HTMLButtonElement, RemoverButtonProperties>(
  ({ ideia, children, ...properties }, reference) => {
    const queryClient = useQueryClient();
    const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

    const { mutate: deleteIdeiaMutation, isPending } = useMutation<TDeleteResponse, Error>({
      mutationFn: () => {
        if (!activeEmpresaId) {
          throw new Error("Empresa não selecionada");
        }
        return deleteIdeia(activeEmpresaId, ideia);
      },
      onSuccess() {
        return queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
      onError(error) {
        captureException(error, true);
      }
    });

    /**
     * Handles the click event on the button.
     * @param event - The event that triggered the click.
     */
    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();

      if (properties.onClick) {
        properties.onClick(event);
      }

      modals.openConfirmModal({
        title: "Remover ideia",
        centered: true,
        children: (
          <Text size="sm">
            Tem certeza que deseja remover a ideia <Mark>{ideia.titulo}</Mark>
          </Text>
        ),
        labels: {
          confirm: "Sim",
          cancel: "Não"
        },
        confirmProps: { color: "red" },
        onConfirm: () => {
          void deleteIdeiaMutation();
        }
      });
    }

    return (
      <UnstyledButton disabled={isPending} ref={reference} {...properties} onClick={handleClick}>
        {children}
      </UnstyledButton>
    );
  }
);

RemoverIdeiaButton.displayName = "RemoverIdeiaButton";

export default RemoverIdeiaButton;
